/**
 * F2 — SALIDA DE PROYECTOR (pantalla limpia).
 *
 * Intentionally dumb page: requires no account, no SQLite, no capability
 * at all — renders slide pushed live by PRESENTER via
 * P2P-LAN (AGPJ1 code) or BroadcastChannel (`?local=1`, second window
 * of same device). Large text, theme-aware, legible from distance.
 */
import { msg } from "@lingui/core/macro";
import { createSignal, Match, onCleanup, onMount, Show, Switch } from "solid-js";
import { MonitorPlay } from "@fleet/icons";
import type { LiveSlide } from "@agape/shared";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { connectProjector, listenLocalProjection, type ProjectorConnection } from "./live";

type Phase = "join" | "connecting" | "live" | "ended" | "error";

export function ProjectorPage(props: { local: boolean }) {
  const { t } = useI18n();
  // oxlint-disable-next-line solid/reactivity -- `local` comes from route query param and is fixed during page life (projector opens in own window)
  const localMode = props.local;
  const [phase, setPhase] = createSignal<Phase>(localMode ? "live" : "join");
  const [slide, setSlide] = createSignal<LiveSlide | null>(null);
  const [blank, setBlank] = createSignal(false);
  const [code, setCode] = createSignal("");

  let connection: ProjectorConnection | undefined;
  let stopLocal: (() => void) | undefined;

  const handleMessage = (message: { type: string; slide?: LiveSlide }) => {
    switch (message.type) {
      case "slide": {
        setBlank(false);
        setSlide(message.slide ?? null);
        break;
      }
      case "blank": {
        setBlank(true);
        break;
      }
      case "bye": {
        setPhase("ended");
        break;
      }
      default: {
        break;
      }
    }
  };

  onMount(() => {
    if (localMode) {
      stopLocal = listenLocalProjection(handleMessage);
    }
  });

  onCleanup(() => {
    connection?.close();
    stopLocal?.();
  });

  const join = async () => {
    setPhase("connecting");
    try {
      connection = await connectProjector(code(), handleMessage, () => {
        setPhase((current) => (current === "live" ? "ended" : current));
      });
      setPhase("live");
    } catch {
      setPhase("error");
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background p-6 text-foreground"
      data-testid="projector-screen"
    >
      <Switch>
        <Match when={phase() === "join" || phase() === "connecting" || phase() === "error"}>
          <Card class="w-full max-w-md">
            <CardContent class="space-y-4 py-6">
              <div class="flex items-center gap-2">
                <MonitorPlay size={20} />
                <h1 class="text-lg font-semibold">{t(msg`Projector screen`)}</h1>
              </div>
              <p class="text-sm text-muted-foreground">
                {t(
                  msg`Enter the pairing code shown on the presenter device. The slides travel directly between the two devices over your local network.`,
                )}
              </p>
              <input
                class="w-full rounded-md border border-border bg-transparent px-3 py-2 font-mono text-sm"
                // oxlint-disable-next-line lingui/no-unlocalized-strings -- pairing code format, not copy
                placeholder="AGPJ1-…"
                value={code()}
                onInput={(e) => setCode(e.currentTarget.value)}
                data-testid="projector-code-input"
                aria-label={t(msg`Pairing code`)}
              />
              <Show when={phase() === "error"}>
                <p class="text-sm text-danger" data-testid="projector-error">
                  {t(
                    msg`Could not connect. Check the code and make sure both devices are on the same network.`,
                  )}
                </p>
              </Show>
              <Button
                class="w-full"
                disabled={phase() === "connecting" || code().trim().length === 0}
                onClick={() => void join()}
                data-testid="projector-join"
              >
                {phase() === "connecting" ? t(msg`Connecting…`) : t(msg`Connect`)}
              </Button>
            </CardContent>
          </Card>
        </Match>
        <Match when={phase() === "ended"}>
          <p class="text-xl text-muted-foreground" data-testid="projector-ended">
            {t(msg`The presenter ended the projection.`)}
          </p>
        </Match>
        <Match when={phase() === "live"}>
          <Show
            when={!blank() && slide()}
            fallback={
              <Show
                when={blank()}
                fallback={
                  <p
                    class="animate-pulse text-xl text-muted-foreground"
                    data-testid="projector-waiting"
                  >
                    {t(msg`Waiting for the presenter…`)}
                  </p>
                }
              >
                <div class="fixed inset-0 bg-background" data-testid="projector-blank" />
              </Show>
            }
          >
            {(current) => (
              <div class="flex h-full w-full flex-col items-center justify-center gap-10">
                <p
                  class="max-w-6xl whitespace-pre-line text-center text-4xl font-semibold leading-tight sm:text-6xl"
                  data-testid="projector-body"
                >
                  {current().body}
                </p>
                <p class="text-lg text-muted-foreground" data-testid="projector-caption">
                  {current().itemTitle}
                  <Show when={current().slideCount > 1}>
                    {" "}
                    · {current().slideIndex + 1}/{current().slideCount}
                  </Show>
                </p>
              </div>
            )}
          </Show>
        </Match>
      </Switch>
    </div>
  );
}
